import { useEffect, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import PrintIcon from "@mui/icons-material/Print";
import { api } from "../lib/api";
import { useSession } from "../auth/AuthProvider";
import { useT } from "../i18n";

interface ContractDocument {
  html: string;
  sha256: string;
  status: string;
  signatures: unknown[];
}

/** Sealed contract viewer — renders exactly the hashed document, sandboxed. */
export function ContractDialog({
  contractId,
  onClose,
}: {
  contractId: string | null;
  onClose: () => void;
}) {
  const { orgId } = useSession();
  const [doc, setDoc] = useState<ContractDocument | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [signing, setSigning] = useState(false);
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const t = useT();

  /**
   * Prints only the sealed contract itself (the iframe document), not the
   * dialog chrome. The frame is same-origin but script-free, so the browser
   * print dialog → PDF/paper yields the exact hashed document.
   */
  function printContract() {
    const win = iframeRef.current?.contentWindow;
    if (!win) return;
    win.focus();
    win.print();
  }

  function reload() {
    if (!contractId) return;
    setDoc(null);
    setError(null);
    api<ContractDocument>(`/contracts/${contractId}/document`)
      .then(setDoc)
      .catch((e) => setError(e instanceof Error ? e.message : t("common_error")));
  }

  useEffect(reload, [contractId]);

  async function signInPerson() {
    if (!contractId || !orgId) return;
    setSigning(true);
    setError(null);
    try {
      await api(`/orgs/${orgId}/contracts/${contractId}/sign-in-person`, { method: "POST" });
      reload();
    } catch (e) {
      setError(e instanceof Error ? e.message : t("contract_sign_failed"));
    } finally {
      setSigning(false);
    }
  }

  if (!contractId) return null;

  return (
    <Dialog open onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Stack direction="row" alignItems="center" spacing={2}>
          <Typography variant="h6" sx={{ flex: 1 }}>
            {t("contract_dialog_title")}
          </Typography>
          {doc && (
            <Chip
              label={doc.status === "signed" ? t("contracts_status_signed") : t("contract_awaiting_customer")}
              color={doc.status === "signed" ? "success" : "warning"}
              size="small"
            />
          )}
          <IconButton onClick={onClose} size="small" aria-label={t("common_close")}>
            <CloseIcon />
          </IconButton>
        </Stack>
      </DialogTitle>
      <DialogContent sx={{ minHeight: 480 }}>
        {error && <Alert severity="error">{error}</Alert>}
        {!doc && !error && (
          <Box display="flex" justifyContent="center" py={8}>
            <CircularProgress />
          </Box>
        )}
        {doc && (
          <>
            <iframe
              ref={iframeRef}
              title="contract"
              srcDoc={doc.html}
              sandbox="allow-same-origin"
              style={{ width: "100%", height: 480, border: "1px solid #ddd", borderRadius: 8 }}
            />
            <Typography variant="caption" color="text.secondary" sx={{ wordBreak: "break-all" }}>
              {t("contract_seal_line", { hash: doc.sha256 })}
            </Typography>
          </>
        )}
      </DialogContent>
      {doc && (
        <DialogActions sx={{ px: 3, pb: 2 }}>
          {doc.status === "issued" && (
            <Typography variant="caption" color="text.secondary" sx={{ flex: 1 }}>
              {t("contract_inperson_note")}
            </Typography>
          )}
          <Button startIcon={<PrintIcon />} onClick={printContract} sx={{ ml: doc.status === "issued" ? 0 : "auto" }}>
            {t("contract_print_pdf")}
          </Button>
          {doc.status === "issued" && (
            <Button variant="contained" onClick={signInPerson} disabled={signing}>
              {signing ? t("contract_signing") : t("contract_sign_inperson")}
            </Button>
          )}
        </DialogActions>
      )}
    </Dialog>
  );
}
